// Initiative passes in the tracker (issue 1525; docs/MRCS/shadowrun-3.md §5.3): one phase per combatant per pass while
// its score less the step per earlier pass is above 0, highest first, each pass on its own line — taken phases dimmed,
// the one acting marked. Drawn from the viewer's own reading (`PlayerEncounter.passes`, the DM's `dmEncounterView`):
// a player's holds the phases of what they see and nothing else, and a score only where they are shown the initiative.
import { passScore, type PlayerEncounter } from '@dragons-table/vtt-shared';

export interface PhaseRow { pass: number; phases: Array<{ id: string; name: string | null; score: number | null; done: boolean; active: boolean }> }

/** The reading's phases grouped by pass, each with its name, its score in that pass (when shown) and whether taken or acting. */
export function phaseRows(enc: PlayerEncounter): PhaseRow[] {
  const ps = enc.passes;
  if (!ps) return [];
  const now = ps.phases.findIndex((p) => !p.done);
  const rows: PhaseRow[] = [];
  ps.phases.forEach((p, i) => {
    const c = enc.combatants.find((x) => x.id === p.id);
    if (!c) return;
    let row = rows.find((r) => r.pass === p.pass);
    if (!row) { row = { pass: p.pass, phases: [] }; rows.push(row); }
    row.phases.push({ id: p.id, name: c.name ?? null, score: passScore({ initRoll: c.initRoll ?? null, scoreModifier: c.scoreModifier ?? 0, woundModifier: c.woundModifier ?? 0 }, p.pass, ps.step), done: p.done, active: i === now && p.id === enc.active });
  });
  return rows;
}

/** "Combat Turn 2 · Pass 3": the round in the system's word for it, and the pass under way; a fight without passes, "Round 2". */
export function roundWords(enc: { round: number; passes?: { pass: number } | undefined }, unit: string | undefined): string {
  return enc.passes ? `${unit ?? 'Round'} ${enc.round} · Pass ${enc.passes.pass}` : `Round ${enc.round}`;
}

export function PhaseList({ enc }: { enc: PlayerEncounter }) {
  const rows = phaseRows(enc);
  if (!enc.passes) return null;
  return (
    <ol className="space-y-1 text-xs" data-testid="phases" aria-label="Phases this round">
      {rows.length === 0 && <li className="text-stone-500">No phases this round.</li>}
      {rows.map((r) => (
        <li key={r.pass} className="flex flex-wrap items-center gap-1" data-testid={`pass-${r.pass}`}>
          <span className="w-12 shrink-0 text-[0.6875rem] text-stone-400">Pass {r.pass}</span>
          {r.phases.map((p) => (
            <span key={p.id} data-testid={`phase-${p.id}-${r.pass}`} data-done={p.done ? 'true' : undefined} aria-current={p.active ? 'true' : undefined}
              className={`rounded px-1.5 py-0.5 ${p.active ? 'bg-amber-600 text-stone-950' : p.done ? 'bg-stone-800 text-stone-500 line-through' : 'bg-stone-800 text-stone-100'}`}>
              {p.name ?? 'Unnamed'}{p.score !== null ? <>{' '}<span className="tabular-nums">{p.score}</span></> : null}
            </span>
          ))}
        </li>
      ))}
    </ol>
  );
}
